import { useEffect, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Heart, MessageCircle, Send, Image as ImageIcon, X, Users, Sparkles, ShieldCheck, ShieldAlert } from "lucide-react";
import MessageButton from "@/components/MessageButton";
import CommunityFilterBar from "@/components/CommunityFilterBar";
import { useCommunityFilter } from "@/hooks/useCommunityFilter";
import { postMatchesInterests, FEED_TOPICS, postMatchesTopic } from "@/lib/interests";
import { formatDistanceToNow } from "date-fns";

function relTime(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
}

function ModerationBadge({ mod }) {
  if (!mod) return null;
  if (mod.risk_level === "pending") {
    return (
      <span
        data-testid="mod-pending-badge"
        className="inline-flex items-center gap-1 rounded-full bg-[#F9F7F4] px-2 py-0.5 text-[10px] font-medium text-[#2E473B]/60"
        title="AI moderation in progress"
      >
        <Sparkles className="h-3 w-3 animate-pulse" /> reviewing
      </span>
    );
  }
  if (mod.risk_level === "caution") {
    return (
      <span
        data-testid="mod-caution-badge"
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
        title={mod.reason || "AI flagged for caution"}
      >
        <ShieldAlert className="h-3 w-3" /> caution
      </span>
    );
  }
  if (mod.helpful) {
    return (
      <span
        data-testid="mod-helpful-badge"
        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
        title="Marked helpful by AI"
      >
        <ShieldCheck className="h-3 w-3" /> helpful
      </span>
    );
  }
  return null;
}

export default function Feed() {
  const { user } = useAuth();
  const community = useCommunityFilter();
  const [posts, setPosts] = useState([]);
  const [interests, setInterests] = useState([]);
  const [onlyForYou, setOnlyForYou] = useState(false);
  const [topic, setTopic] = useState("all");
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("community");
  const [imagePath, setImagePath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [commentsMap, setCommentsMap] = useState({});
  const [commentDraft, setCommentDraft] = useState({});

  const load = async () => {
    try {
      const params = community.filterParam ? { community: community.filterParam } : {};
      const { data } = await api.get("/feed", { params });
      setPosts(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = "Community Feed · Jirani"; }, []);
  useEffect(() => {
    if (!user) { setInterests([]); return; }
    api.get("/preferences").then(({ data }) => setInterests(data.interests || [])).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (community.loading) return;
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community.filterParam, community.loading]);

  // Poll for moderation updates on any 'pending' posts (fire-and-forget on backend)
  useEffect(() => {
    const pending = posts.filter((p) => p.moderation?.risk_level === "pending");
    if (pending.length === 0) return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/feed");
        setPosts(data);
      } catch { /* ignore */ }
    }, 4000);
    return () => clearTimeout(timer);
  }, [posts]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagePath(data.path);
      toast.success("Image ready");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/feed", { content, category, image_path: imagePath });
      setPosts([data, ...posts]);
      setContent(""); setImagePath(null); setCategory("community");
      toast.success("Posted!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  const toggleLike = async (post) => {
    if (!user) return toast("Please log in to react.");
    try {
      const { data } = await api.post(`/feed/${post.id}/like`);
      setPosts((ps) => ps.map((p) => p.id === post.id ? {
        ...p,
        likes: data.liked
          ? Array.from(new Set([...(p.likes || []), user.user_id]))
          : (p.likes || []).filter((u) => u !== user.user_id),
      } : p));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleComments = async (post) => {
    setOpenComments((o) => ({ ...o, [post.id]: !o[post.id] }));
    if (!commentsMap[post.id]) {
      try {
        const { data } = await api.get(`/feed/${post.id}/comments`);
        setCommentsMap((m) => ({ ...m, [post.id]: data }));
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const submitComment = async (post) => {
    const text = (commentDraft[post.id] || "").trim();
    if (!text) return;
    try {
      const { data } = await api.post(`/feed/${post.id}/comments`, { content: text });
      setCommentsMap((m) => ({ ...m, [post.id]: [...(m[post.id] || []), data] }));
      setCommentDraft((d) => ({ ...d, [post.id]: "" }));
      setPosts((ps) => ps.map((p) => p.id === post.id ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="feed-page" className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-8">
        <div className="jirani-eyebrow">Habari · Community Feed</div>
        <h1 className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 800 }}>
          <Users className="h-8 w-8 text-[#D95D39]" /> What's happening jirani
        </h1>
        <p className="mt-2 text-[#4A4A4A]">Say hi, ask questions, share tips. Your neighbors are listening.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <CommunityFilterBar
            active={community.active}
            showAll={community.showAll}
            onToggle={community.setShowAll}
          />
          {interests.length > 0 && (
            <button
              data-testid="only-for-you-toggle"
              onClick={() => setOnlyForYou((v) => !v)}
              className={`jirani-pill inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${
                onlyForYou
                  ? "bg-[#D95D39] text-white"
                  : "border border-black/5 bg-white text-[#2E473B] hover:bg-[#F9F7F4]"
              }`}
              title="Show posts matching your interests"
            >
              <Sparkles className="h-3 w-3" /> {onlyForYou ? "Showing For you" : "Only For you"}
            </button>
          )}
          <div
            data-testid="ai-mod-note"
            className="inline-flex items-center gap-2 rounded-full border border-black/5 bg-white px-3 py-1 text-[11px] text-[#2E473B]/80"
          >
            <Sparkles className="h-3 w-3 text-[#D95D39]" />
            Gently moderated by Claude
          </div>
        </div>

        <div
          data-testid="feed-topic-chips"
          className="mt-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
        >
          {FEED_TOPICS.map((t) => {
            const active = topic === t.id;
            return (
              <button
                key={t.id}
                data-testid={`feed-topic-chip-${t.id}`}
                onClick={() => setTopic(t.id)}
                className={`jirani-pill shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "bg-[#2E473B] text-white"
                    : "border border-black/10 bg-white text-[#2E473B] hover:bg-[#F9F7F4]"
                }`}
              >
                <span className="mr-1 opacity-70">{t.emoji}</span>
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      {user && (
        <form
          onSubmit={submit}
          data-testid="new-post-form"
          className="jirani-card mb-8 rounded-2xl border border-black/5 bg-white p-5"
        >
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#2E473B] text-sm font-semibold text-white">
              {user.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="flex-1">
              <Textarea
                data-testid="new-post-textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Habari yako, ${user.name?.split(" ")[0] || "jirani"}?`}
                rows={3}
                className="min-h-[86px] resize-none rounded-xl border-black/10 bg-[#F9F7F4] focus-visible:ring-[#D95D39]"
              />

              {imagePath && (
                <div className="relative mt-3 inline-block">
                  <img
                    src={fileUrl(imagePath)}
                    alt="preview"
                    className="max-h-48 rounded-xl object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setImagePath(null)}
                    className="absolute -right-2 -top-2 rounded-full bg-[#C1292E] p-1 text-white"
                    data-testid="remove-image-btn"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    data-testid="new-post-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="h-9 rounded-full border border-black/10 bg-white px-3 text-xs text-[#2E473B] focus:outline-none focus:ring-2 focus:ring-[#D95D39]"
                  >
                    <option value="community">Community</option>
                    <option value="help">Help / Recommendations</option>
                    <option value="lost_found">Lost & Found</option>
                    <option value="meetup">Meetup</option>
                  </select>

                  <label
                    data-testid="upload-image-label"
                    className="jirani-pill inline-flex cursor-pointer items-center gap-1 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs text-[#2E473B] hover:bg-[#F9F7F4]"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    {uploading ? "Uploading..." : "Add image"}
                    <input
                      data-testid="upload-image-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => upload(e.target.files?.[0])}
                    />
                  </label>
                </div>

                <Button
                  data-testid="submit-post-btn"
                  type="submit"
                  disabled={posting || !content.trim()}
                  className="jirani-pill rounded-full bg-[#D95D39] px-5 text-white hover:bg-[#c25333]"
                >
                  <Send className="mr-2 h-4 w-4" /> {posting ? "Posting..." : "Post"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-2xl border border-black/5 bg-white/60" />
          ))}
        </div>
      ) : (
        <ul data-testid="posts-list" className="space-y-5">
          {(() => {
            let list = posts.map((p) => ({ ...p, _match: postMatchesInterests(p, interests) }));
            if (topic !== "all") list = list.filter((p) => postMatchesTopic(p, topic));
            if (onlyForYou) list = list.filter((p) => p._match);
            // Rank matched posts to the top while keeping recency inside groups
            list.sort((a, b) => {
              if (a._match !== b._match) return a._match ? -1 : 1;
              return 0;
            });
            return list;
          })().map((p) => {
            const liked = user && (p.likes || []).includes(user.user_id);
            return (
              <li
                key={p.id}
                data-testid={`post-${p.id}`}
                className={`jirani-card rounded-2xl border bg-white p-5 ${
                  p._match ? "border-[#D95D39]/30 ring-1 ring-[#D95D39]/20" : "border-black/5"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F4A261]/25 text-sm font-semibold text-[#2E473B]">
                    {(p.author?.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <div className="font-semibold text-[#1A1A1A]">{p.author?.name || "A neighbor"}</div>
                      {p.author?.neighborhood && (
                        <div className="text-xs text-[#2E473B]/70">· {p.author.neighborhood}</div>
                      )}
                      <div className="text-xs text-[#4A4A4A]">· {relTime(p.created_at)}</div>
                      <div className="ml-auto flex items-center gap-1.5">
                        {p._match && (
                          <span
                            data-testid={`for-you-badge-${p.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#D95D39] px-2 py-0.5 text-[10px] font-semibold text-white"
                            title="Matches your interests"
                          >
                            <Sparkles className="h-3 w-3" /> For you
                          </span>
                        )}
                        <ModerationBadge mod={p.moderation} />
                        <Badge className="rounded-full bg-[#F9F7F4] text-[#2E473B] hover:bg-[#F9F7F4]">
                          {p.category || "community"}
                        </Badge>
                      </div>
                    </div>

                    <p className="mt-3 whitespace-pre-wrap text-[#1A1A1A]">{p.content}</p>

                    {p.image_path && (
                      <div className="mt-3 overflow-hidden rounded-xl border border-black/5">
                        <img src={fileUrl(p.image_path)} alt="" className="w-full object-cover" />
                      </div>
                    )}

                    <div className="mt-4 flex items-center gap-4 text-sm">
                      <button
                        data-testid={`like-btn-${p.id}`}
                        onClick={() => toggleLike(p)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors ${
                          liked ? "text-[#D95D39]" : "text-[#4A4A4A] hover:text-[#D95D39]"
                        }`}
                      >
                        <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
                        {(p.likes || []).length}
                      </button>
                      <button
                        data-testid={`comments-btn-${p.id}`}
                        onClick={() => toggleComments(p)}
                        className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[#4A4A4A] hover:text-[#2E473B]"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {p.comment_count || 0}
                      </button>
                      {p.author?.user_id && (
                        <div className="ml-auto">
                          <MessageButton peerId={p.author.user_id} peerName={p.author.name} />
                        </div>
                      )}
                    </div>

                    {openComments[p.id] && (
                      <div className="mt-4 border-t border-black/5 pt-4">
                        <div className="space-y-3">
                          {(commentsMap[p.id] || []).map((c) => (
                            <div key={c.id} className="flex items-start gap-2">
                              <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#2E473B] text-[10px] font-semibold text-white">
                                {(c.author?.name || "?").charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 rounded-2xl bg-[#F9F7F4] px-3 py-2">
                                <div className="flex items-baseline gap-2">
                                  <div className="text-xs font-semibold text-[#1A1A1A]">
                                    {c.author?.name || "Neighbor"}
                                  </div>
                                  <div className="text-[10px] text-[#4A4A4A]">{relTime(c.created_at)}</div>
                                </div>
                                <div className="text-sm text-[#1A1A1A]">{c.content}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {user && (
                          <div className="mt-3 flex items-center gap-2">
                            <input
                              data-testid={`comment-input-${p.id}`}
                              value={commentDraft[p.id] || ""}
                              onChange={(e) => setCommentDraft((d) => ({ ...d, [p.id]: e.target.value }))}
                              onKeyDown={(e) => e.key === "Enter" && submitComment(p)}
                              placeholder="Say something kind..."
                              className="h-10 flex-1 rounded-full border border-black/10 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#D95D39]"
                            />
                            <button
                              data-testid={`comment-submit-${p.id}`}
                              onClick={() => submitComment(p)}
                              className="jirani-pill rounded-full bg-[#2E473B] px-4 py-2 text-xs font-semibold text-white"
                            >
                              Reply
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
          {posts.length === 0 && (
            <div className="rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center text-[#4A4A4A]">
              No posts yet. Be the first to say habari!
            </div>
          )}
        </ul>
      )}
    </div>
  );
}
